import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { apiRequest } from "@/lib/queryClient";
import { canExportAnalytics } from "@/lib/analytics-export";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  Bot, MessageSquare, Globe, FileText, Plus, ArrowRight, Zap, Layers, Crown,
  TrendingUp, TrendingDown, Star, Activity, Users, Radio, Cpu,
} from "lucide-react";
import { Link } from "wouter";
import { useAuth, hasPermission } from "@/lib/auth";
import { useBranding } from "@/hooks/use-branding";
import { BotStatusToggle } from "@/components/bot-status-toggle";
import { PageShell, PageStat } from "@/components/page-shell";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import type { Chatbot } from "@shared/schema";

interface DashboardAnalytics {
  range: { days: number; since: string; until: string };
  plan: {
    name: string;
    botLimit: number;
    agentLimit: number;
    monthlyMessageLimit: number;
    messagesUsed: number;
    botsUsed: number;
    agentsUsed: number;
    subscriptionStatus: string | null;
  } | null;
  platform: {
    organizations: number;
    messagesUsed: number;
    monthlyMessageLimit: number;
    botsUsed: number;
    botLimit: number;
    agentsUsed: number;
    agentLimit: number;
  } | null;
  totals: {
    bots: number;
    activeBots: number;
    conversations: number;
    conversationsInRange: number;
    conversationsPrevRange: number;
    messagesAllTime: number;
    messagesInRange: number;
    ratingsInRange: number;
    averageRating: number;
    tokensInRange: number;
    promptTokensInRange: number;
    completionTokensInRange: number;
    tokensAllTime: number;
    promptTokensAllTime: number;
    completionTokensAllTime: number;
  };
  trend: { date: string; messages: number; userMessages: number; botMessages: number; tokens: number }[];
  topBots: { id: string; name: string; type: string; color: string; conversations: number; messages: number }[];
  channels: { channel: string; count: number }[];
  statuses: { status: string; count: number }[];
  ratingHistogram: { stars: number; count: number }[];
}

// Rough estimate of LLM spend from token counts. Uses representative blended
// rates (gpt-4o-mini-class: $0.15 / 1M prompt, $0.60 / 1M completion) — this is
// only an indicative figure shown next to the token total, not billing-accurate.
function estimateTokenCost(promptTokens: number, completionTokens: number): number {
  const PROMPT_RATE = 0.15 / 1_000_000;
  const COMPLETION_RATE = 0.6 / 1_000_000;
  return promptTokens * PROMPT_RATE + completionTokens * COMPLETION_RATE;
}

function formatUsd(n: number): string {
  if (n <= 0) return "$0.00";
  if (n < 0.01) return "<$0.01";
  return "$" + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function ChatbotTypeIcon({ type }: { type: string }) {
  switch (type) {
    case "website": return <Globe className="w-4 h-4" aria-hidden="true" />;
    case "document": return <FileText className="w-4 h-4" aria-hidden="true" />;
    case "combo": return <Layers className="w-4 h-4" aria-hidden="true" />;
    case "ai-enhanced": return <Zap className="w-4 h-4" aria-hidden="true" />;
    default: return <Bot className="w-4 h-4" aria-hidden="true" />;
  }
}

// Calm donut palette — same hues used elsewhere in the app so the
// channel/status charts feel native to the design.
const PALETTE = ["#0c3547", "#2dd4bf", "#a78bfa", "#fb923c", "#f472b6", "#60a5fa", "#facc15", "#34d399"];

function trendDelta(curr: number, prev: number): { pct: number; up: boolean } {
  if (prev === 0) return { pct: curr > 0 ? 100 : 0, up: curr > 0 };
  const pct = Math.round(((curr - prev) / prev) * 100);
  return { pct: Math.abs(pct), up: pct >= 0 };
}

function UtilBar({ label, used, limit, hint, upgradeCopy = true }: { label: string; used: number; limit: number; hint?: string; upgradeCopy?: boolean }) {
  // limit === 0 means unlimited — show count only, no progress bar, no denominator.
  const unlimited = limit <= 0;
  const pct = !unlimited ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const exceeded = !unlimited && used >= limit;
  const danger = exceeded || pct >= 90;
  const warn = !danger && pct >= 75;
  return (
    <div className="space-y-1.5" data-testid={`util-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-baseline justify-between text-xs">
        <span className="font-medium">{label}</span>
        <span className={`tabular-nums ${exceeded ? "font-semibold text-rose-600" : "text-muted-foreground"}`}>
          {unlimited
            ? used.toLocaleString()
            : `${used.toLocaleString()} / ${limit.toLocaleString()}`}
        </span>
      </div>
      {!unlimited && (
        <Progress value={pct} className={`h-1.5 ${danger ? "[&>div]:bg-rose-500" : warn ? "[&>div]:bg-amber-500" : ""}`} aria-label={`${label}: ${pct}% used`} />
      )}
      {exceeded && upgradeCopy ? (
        <p className="text-[11px] font-medium text-rose-600" data-testid={`util-${label.toLowerCase().replace(/\s+/g, "-")}-exceeded`}>
          Limit reached — upgrade your plan to continue.
        </p>
      ) : hint && (!exceeded || !upgradeCopy) ? (
        <p className="text-[11px] text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

export default function Dashboard() {
  const branding = useBranding();
  const { user, organization } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const canExport = canExportAnalytics(user?.role);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  async function exportAnalytics() {
    if (exporting) return;
    setExporting(true);
    setExportError("");
    try {
      const response = await apiRequest("GET", "/api/analytics/dashboard/export?days=30");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "analytics-30-days.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setExportError("Analytics export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  }

  const { data: chatbots, isLoading: botsLoading } = useQuery<Chatbot[]>({
    queryKey: ["/api/chatbots"],
  });

  const { data: analytics, isLoading: analyticsLoading } = useQuery<DashboardAnalytics>({
    queryKey: ["/api/analytics/dashboard"],
    refetchInterval: 60_000,
  });

  const isLoading = botsLoading || analyticsLoading;
  const totals = analytics?.totals;
  const plan = analytics?.plan;
  const platform = analytics?.platform;
  const delta = trendDelta(totals?.conversationsInRange ?? 0, totals?.conversationsPrevRange ?? 0);

  const stats = isLoading ? (
    Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="stat-chip p-4">
        <Skeleton className="h-3 w-20 mb-3" />
        <Skeleton className="h-7 w-16" />
        <Skeleton className="h-3 w-28 mt-2" />
      </div>
    ))
  ) : (
    <>
      <PageStat
        label="Active chatbots"
        value={`${totals?.activeBots ?? 0} / ${totals?.bots ?? 0}`}
        hint="Currently serving"
        icon={<Bot className="w-4 h-4" aria-hidden="true" />}
      />
      <PageStat
        label="Conversations"
        value={(totals?.conversationsInRange ?? 0).toLocaleString()}
        hint={`Last ${analytics?.range.days ?? 30} days`}
        icon={<MessageSquare className="w-4 h-4" aria-hidden="true" />}
      />
      <PageStat
        label="Messages"
        value={(totals?.messagesInRange ?? 0).toLocaleString()}
        hint={`${(totals?.messagesAllTime ?? 0).toLocaleString()} all-time`}
        icon={<Activity className="w-4 h-4" aria-hidden="true" />}
      />
      <PageStat
        label="Tokens used"
        value={
          <>
            {(totals?.tokensInRange ?? 0).toLocaleString()}
            <span
              className="ml-1 text-base font-normal text-muted-foreground/60"
              title="Estimated cost (indicative, not billing-accurate)"
              data-testid="text-token-cost"
            >
              ({formatUsd(estimateTokenCost(totals?.promptTokensInRange ?? 0, totals?.completionTokensInRange ?? 0))})
            </span>
          </>
        }
        hint={`${(totals?.tokensAllTime ?? 0).toLocaleString()} all-time`}
        icon={<Cpu className="w-4 h-4" aria-hidden="true" />}
      />
      <PageStat
        label="Avg. rating"
        value={totals?.ratingsInRange ? `${totals.averageRating.toFixed(1)} ★` : "—"}
        hint={`${totals?.ratingsInRange ?? 0} ratings`}
        icon={<Star className="w-4 h-4" aria-hidden="true" />}
      />
    </>
  );

  return (
    <PageShell
      eyebrow={organization?.name}
      title="Dashboard"
      subtitle={`Manage your ${branding.shortName} chatbots and monitor performance.`}
      actions={
        <>
          {canExport && <Button variant="outline" disabled={exporting} onClick={exportAnalytics}
            data-testid="button-export-analytics">{exporting ? "Exporting…" : "Export analytics CSV"}</Button>}
          {exportError && <span role="alert" className="text-sm text-destructive">{exportError}</span>}
          {organization && !isSuperAdmin && (
            <div className="hidden sm:flex items-center gap-2 mr-1">
              <Badge variant="outline" className="text-[10px] capitalize">{organization.plan}</Badge>
              {plan?.subscriptionStatus ? (
                <Badge
                  variant={plan.subscriptionStatus === "active" ? "default" : "secondary"}
                  className="text-[10px] capitalize"
                  data-testid="badge-subscription-status"
                >
                  {plan.subscriptionStatus}
                </Badge>
              ) : null}
            </div>
          )}
          {isSuperAdmin && (
            <Badge variant="outline" className="text-[10px] mr-1 hidden sm:inline-flex">
              <Crown className="w-3 h-3 mr-1 text-amber-500" aria-hidden="true" />Super Admin
            </Badge>
          )}
          {isSuperAdmin ? (
            <Link href="/admin">
              <Button variant="outline" size="sm" data-testid="button-admin-panel">
                <Crown className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />Admin
              </Button>
            </Link>
          ) : (
            <Link href="/settings">
              <Button variant="outline" size="sm" data-testid="button-manage-plan">
                <Zap className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />Plan
              </Button>
            </Link>
          )}
          <Link href="/chatbots/new">
            <Button data-testid="button-create-chatbot">
              <Plus className="w-4 h-4 mr-2" aria-hidden="true" />Create Chatbot
            </Button>
          </Link>
        </>
      }
      stats={stats}
    >
      {/* Legacy test-ids — preserved as hidden compat spans for older tests. */}
      <div className="hidden">
        <span data-testid="text-org-dashboard-name">{organization?.name ?? ""}</span>
        <span data-testid="stat-total-chatbots">{chatbots?.length ?? 0}</span>
        <span data-testid="stat-active-bots">{chatbots?.filter((b) => b.status === "active").length ?? 0}</span>
        <span data-testid="stat-website-bots">{chatbots?.filter((b) => b.type === "website").length ?? 0}</span>
        <span data-testid="stat-document-bots">{chatbots?.filter((b) => b.type === "document").length ?? 0}</span>
      </div>

      {/* ── Plan utilization + headline trend ─────────────────────── */}
      <div className="grid gap-4 lg:grid-cols-3">
        {/* Utilization: super admins see platform-wide totals (they don't
            belong to a tenant plan); everyone else sees their own org's
            plan utilization with upgrade affordances. */}
        {isSuperAdmin ? (
          <Card data-testid="card-platform-utilization">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Platform utilization</CardTitle>
              <CardDescription className="text-xs">
                Totals across all {platform ? platform.organizations.toLocaleString() : ""} tenant organizations.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {analyticsLoading || !platform ? (
                <>
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </>
              ) : (
                <>
                  <UtilBar label="Messages this month" used={platform.messagesUsed} limit={0} upgradeCopy={false} hint="Total messages sent across all tenant plans this month." />
                  <UtilBar label="Chatbots" used={platform.botsUsed} limit={platform.botLimit} upgradeCopy={false} hint="Active chatbots / total allowed across tenant plans." />
                  <UtilBar label="Operator seats" used={platform.agentsUsed} limit={0} upgradeCopy={false} hint="Active tenant users across all organizations." />
                </>
              )}
            </CardContent>
          </Card>
        ) : (
        <Card data-testid="card-plan-utilization">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <CardTitle className="text-base">Plan utilization</CardTitle>
                <CardDescription className="text-xs">
                  {plan?.name ? <span className="capitalize">{plan.name}</span> : "—"} plan
                </CardDescription>
              </div>
              {hasPermission(user, "billing.manage") ? (
                <Link href="/settings">
                  <Button variant="ghost" size="sm" className="h-7 text-xs" data-testid="link-plan-upgrade">
                    Upgrade
                    <ArrowRight className="w-3 h-3 ml-1" aria-hidden="true" />
                  </Button>
                </Link>
              ) : null}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {analyticsLoading || !plan ? (
              <>
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </>
            ) : (
              <>
                <UtilBar
                  label="Messages this month"
                  used={plan.messagesUsed}
                  limit={0}
                />
                <UtilBar label="Chatbots" used={plan.botsUsed} limit={plan.botLimit} />
                <UtilBar label="Operator seats" used={plan.agentsUsed} limit={0} hint="Active users in this workspace." />
              </>
            )}
          </CardContent>
        </Card>
        )}

        {/* 30-day message trend */}
        <Card className="lg:col-span-2" data-testid="card-message-trend">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  Conversation activity
                  {totals && (
                    <Badge
                      variant="outline"
                      className={`text-[10px] gap-1 ${delta.up ? "text-emerald-600 border-emerald-200 bg-emerald-50 dark:bg-emerald-950/40" : "text-rose-600 border-rose-200 bg-rose-50 dark:bg-rose-950/40"}`}
                      data-testid="badge-trend-delta"
                    >
                      {delta.up ? <TrendingUp className="w-3 h-3" aria-hidden="true" /> : <TrendingDown className="w-3 h-3" aria-hidden="true" />}
                      {delta.pct}%
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription className="text-xs">
                  Messages per day · last {analytics?.range.days ?? 30} days
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {analyticsLoading ? (
              <Skeleton className="h-[220px] w-full" />
            ) : (
              <div role="img" aria-label={`Conversation activity chart — messages per day over the last ${analytics?.range.days ?? 30} days`}>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={analytics?.trend ?? []} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="msgGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(199 89% 48%)" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="hsl(199 89% 48%)" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="tokGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(262 70% 60%)" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="hsl(262 70% 60%)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                    tickFormatter={(d) => {
                      const dt = new Date(d);
                      return `${dt.getMonth() + 1}/${dt.getDate()}`;
                    }}
                    interval="preserveStartEnd"
                    minTickGap={30}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    yAxisId="msg"
                    tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                    width={28}
                    allowDecimals={false}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    yAxisId="tok"
                    orientation="right"
                    tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                    width={42}
                    allowDecimals={false}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : `${v}`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    labelFormatter={(d) => new Date(d).toLocaleDateString()}
                    formatter={(value: any, name: any) => [Number(value).toLocaleString(), name]}
                  />
                  <Area
                    yAxisId="msg"
                    type="monotone"
                    dataKey="messages"
                    stroke="hsl(199 89% 48%)"
                    strokeWidth={2}
                    fill="url(#msgGrad)"
                    name="Messages"
                  />
                  <Area
                    yAxisId="tok"
                    type="monotone"
                    dataKey="tokens"
                    stroke="hsl(262 70% 60%)"
                    strokeWidth={1.5}
                    fill="url(#tokGrad)"
                    name="Tokens"
                  />
                </AreaChart>
              </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Top bots / channels / ratings ─────────────────────────── */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2" data-testid="card-top-bots">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Top performing chatbots</CardTitle>
            <CardDescription className="text-xs">Messages handled in the selected window.</CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            {analyticsLoading ? (
              <Skeleton className="h-[220px] w-full" />
            ) : !analytics?.topBots.length ? (
              <div className="h-[220px] flex flex-col items-center justify-center text-center text-muted-foreground gap-2">
                <Bot className="w-8 h-8 opacity-40" aria-hidden="true" />
                <p className="text-sm">No conversations recorded yet.</p>
              </div>
            ) : (
              <div role="img" aria-label="Top performing chatbots — bar chart of message counts per chatbot">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={analytics.topBots} layout="vertical" margin={{ top: 5, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 11, fill: "hsl(var(--foreground))" }}
                    width={120}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  />
                  <Bar dataKey="messages" name="Messages" radius={[0, 4, 4, 0]}>
                    {analytics.topBots.map((b, i) => (
                      <Cell key={b.id} fill={b.color || PALETTE[i % PALETTE.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="card-channels">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Radio className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
              Channel mix
            </CardTitle>
            <CardDescription className="text-xs">Where conversations originate.</CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            {analyticsLoading ? (
              <Skeleton className="h-[220px] w-full" />
            ) : !analytics?.channels.length ? (
              <div className="h-[220px] flex flex-col items-center justify-center text-center text-muted-foreground gap-2">
                <Radio className="w-8 h-8 opacity-40" aria-hidden="true" />
                <p className="text-sm">No channel data.</p>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div role="img" aria-label="Channel mix donut chart — shows the distribution of conversations by channel">
                <ResponsiveContainer width="60%" height={180}>
                  <PieChart>
                    <Pie
                      data={analytics.channels}
                      dataKey="count"
                      nameKey="channel"
                      innerRadius={42}
                      outerRadius={70}
                      paddingAngle={2}
                      stroke="hsl(var(--background))"
                      strokeWidth={2}
                    >
                      {analytics.channels.map((_, i) => (
                        <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                </div>
                <ul className="flex-1 space-y-1.5 text-xs">
                  {analytics.channels.slice(0, 6).map((c, i) => (
                    <li key={c.channel} className="flex items-center gap-2" data-testid={`legend-channel-${c.channel}`}>
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: PALETTE[i % PALETTE.length] }} />
                      <span className="capitalize flex-1 truncate">{c.channel}</span>
                      <span className="tabular-nums text-muted-foreground">{c.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Quick actions ─────────────────────────────────────────── */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Link href="/chatbots/new" className="block">
          <div className="feature-tile h-full" data-testid="quick-action-create-bot">
            <div className="feature-tile-icon" style={{ background: "linear-gradient(135deg, hsl(160 70% 45%), hsl(170 70% 40%))" }}>
              <Plus className="w-4 h-4" aria-hidden="true" />
            </div>
            <div className="font-medium text-sm">New chatbot</div>
            <p className="text-xs text-muted-foreground mt-1">Spin up an assistant in minutes.</p>
          </div>
        </Link>
        <Link href="/conversations" className="block">
          <div className="feature-tile h-full" data-testid="quick-action-conversations">
            <div className="feature-tile-icon" style={{ background: "linear-gradient(135deg, hsl(262 70% 60%), hsl(280 70% 55%))" }}>
              <MessageSquare className="w-4 h-4" aria-hidden="true" />
            </div>
            <div className="font-medium text-sm">Conversations</div>
            <p className="text-xs text-muted-foreground mt-1">Review what your bots are saying.</p>
          </div>
        </Link>
        {(isSuperAdmin || (((organization as any)?.features ?? []).includes("integrations") && hasPermission(user, "integrations.manage"))) && <Link href="/integrations" className="block">
          <div className="feature-tile h-full" data-testid="quick-action-integrations">
            <div className="feature-tile-icon" style={{ background: "linear-gradient(135deg, hsl(28 92% 48%), hsl(18 92% 42%))" }}>
              <Layers className="w-4 h-4" aria-hidden="true" />
            </div>
            <div className="font-medium text-sm">Integrations</div>
            <p className="text-xs text-muted-foreground mt-1">Slack, Teams, ServiceNow & more.</p>
          </div>
        </Link>}
        <Link href={isSuperAdmin ? "/admin" : "/settings"} className="block">
          <div className="feature-tile h-full" data-testid="quick-action-settings">
            <div className="feature-tile-icon" style={{ background: "linear-gradient(135deg, hsl(340 80% 60%), hsl(320 75% 55%))" }}>
              {isSuperAdmin ? <Crown className="w-4 h-4" aria-hidden="true" /> : <Zap className="w-4 h-4" aria-hidden="true" />}
            </div>
            <div className="font-medium text-sm">{isSuperAdmin ? "Admin console" : "Plan & billing"}</div>
            <p className="text-xs text-muted-foreground mt-1">{isSuperAdmin ? "Tenants, plans, master admins." : "Upgrade or manage your plan."}</p>
          </div>
        </Link>
      </div>

      {/* ── Bots list ─────────────────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between gap-2 mb-4">
          <h2 className="text-lg font-semibold">Your Chatbots</h2>
          <Link href="/chatbots">
            <Button variant="ghost" size="sm" data-testid="link-view-all-chatbots">
              View all
              <ArrowRight className="w-4 h-4 ml-1" aria-hidden="true" />
            </Button>
          </Link>
        </div>

        {botsLoading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <Skeleton className="h-5 w-32 mb-3" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-24" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : chatbots && chatbots.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {chatbots.slice(0, 6).map((bot) => (
              <Link key={bot.id} href={`/chatbots/${bot.id}`}>
                <Card className="cursor-pointer hover-elevate transition-all" data-testid={`card-chatbot-${bot.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-md flex items-center justify-center"
                          style={{ backgroundColor: bot.primaryColor || "#0c3547" }}
                        >
                          <ChatbotTypeIcon type={bot.type} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-sm" data-testid={`text-bot-name-${bot.id}`}>{bot.name}</h3>
                          <p className="text-xs text-muted-foreground capitalize">{bot.type} chatbot</p>
                        </div>
                      </div>
                      {user && ["admin", "super_admin", "org_admin", "it_admin", "hr_admin", "bot_admin"].includes(user.role || "") ? (
                        <BotStatusToggle bot={bot} />
                      ) : (
                        <Badge variant={bot.status === "active" ? "default" : "secondary"} data-testid={`badge-status-${bot.id}`}>
                          {bot.status === "active" ? "Enabled" : "Disabled"}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-3 line-clamp-2">
                      {bot.systemPrompt || "No system prompt configured"}
                    </p>
                    <div className="flex items-center gap-2 mt-3">
                      <Badge variant="outline" className="text-[10px]">{bot.model}</Badge>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <Bot className="w-8 h-8 text-muted-foreground" aria-hidden="true" />
              </div>
              <h2 className="text-lg font-semibold mb-1" data-testid="text-empty-state">No chatbots yet</h2>
              <p className="text-sm text-muted-foreground mb-4 text-center max-w-md">
                Create your first AI chatbot to get started. Choose from website, ChatGPT, or document-based bots.
              </p>
              <Link href="/chatbots/new">
                <Button data-testid="button-create-first-chatbot">
                  <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
                  Create Your First Chatbot
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>

    </PageShell>
  );
}
